import React, { useState, useEffect } from 'react';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import MinimalNavbar from './MinimalNavbar';
import ResponsiveSidebar from './ResponsiveSidebar';
import SidebarEdgeRail from './SidebarEdgeRail';
import { isRTL as getIsRTL } from '../../../utils/rtlUtils';
import ChatbotContainer from '../../chatbot/ChatbotContainer';
import { Fab, Zoom } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useChatbot } from '../../../contexts/ChatbotContext';

export default function ResponsiveLayout({ children, onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isChatbotFullWidth = useMediaQuery(theme.breakpoints.down('sm'));
  const isChatbotCompact = useMediaQuery('(min-width: 901px) and (max-width: 1200px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isRTL, setIsRTL] = useState(() => getIsRTL());
  const [isChatbotEnlarged, setIsChatbotEnlarged] = useState(false);
  const { mainContentRef } = React.useContext(LayoutContext);
  const { isChatbotOpen, toggleChatbot } = useChatbot();
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  // Listen for direction changes
  useEffect(() => {
    const checkDirection = () => {
      setIsRTL(getIsRTL());
    };
    
    // Check immediately
    checkDirection();
    
    // Create a MutationObserver to watch for dir attribute changes
    const observer = new MutationObserver(checkDirection);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir']
    });
    
    return () => observer.disconnect();
  }, []);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarOpen(!sidebarOpen);
    }
  };

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleChatbotEnlarge = () => {
    setIsChatbotEnlarged(prevState => !prevState);
  };

  const chatbotSideOffset = isChatbotFullWidth ? '12px' : '30px';
  const chatbotTopOffset = isChatbotFullWidth ? 'auto' : '94px';
  const chatbotBaseWidth = isChatbotFullWidth ? 'calc(100vw - 24px)' : (isChatbotCompact ? '320px' : '340px');
  const chatbotEnlargedWidth = isChatbotFullWidth ? '100vw' : (isChatbotCompact ? 'min(620px, calc(100vw - 40px))' : 'min(700px, calc(100vw - 60px))');

  const baseChatbotStyle = {
    position: 'fixed',
    bottom: isChatbotFullWidth ? '88px' : '70px',
    top: chatbotTopOffset,
    zIndex: 1000,
    width: chatbotBaseWidth,
    maxWidth: chatbotBaseWidth,
    height: isChatbotFullWidth ? 'min(520px, calc(100dvh - 180px))' : 'auto',
    maxHeight: isChatbotFullWidth ? 'min(560px, calc(100dvh - 164px))' : 'calc(100dvh - 164px)',
    display: isChatbotOpen ? 'block' : 'none',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    borderRadius: isChatbotFullWidth ? '18px' : '15px',
    overflow: 'hidden',
    transition: 'width 0.3s ease, height 0.3s ease, inset 0.3s ease',
  };

  if (isRTL) {
    baseChatbotStyle.left = chatbotSideOffset;
  } else {
    baseChatbotStyle.right = chatbotSideOffset;
  }

  const enlargedChatbotStyle = {
    width: chatbotEnlargedWidth,
    maxWidth: chatbotEnlargedWidth,
    top: isChatbotFullWidth ? '10px' : '80px',
    height: isChatbotFullWidth ? 'calc(100dvh - 20px)' : 'calc(100dvh - 120px)',
    maxHeight: '95vh',
    bottom: isChatbotFullWidth ? '10px' : '40px',
    left: isChatbotFullWidth ? 0 : baseChatbotStyle.left,
    right: isChatbotFullWidth ? 0 : baseChatbotStyle.right,
    borderRadius: isChatbotFullWidth ? '18px' : '20px',
    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%',
      background: '#f9fafb',
      margin: 0,
      padding: 0,
      overflow: 'hidden'
    }}>
      <MinimalNavbar onLogout={onLogout} onToggleSidebar={handleToggleSidebar} />
      
      <ResponsiveSidebar 
        open={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileOpen}
        onMobileToggle={handleMobileToggle}
        onLogout={onLogout}
      />

      {!isMobile && (
        <SidebarEdgeRail
          open={sidebarOpen}
          onToggle={handleToggleSidebar}
          isRTL={isRTL}
        />
      )}
      
      <Box
        ref={mainContentRef}
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          transition: 'margin-left 0.3s ease, margin-right 0.3s ease',
          p: 0,
          minWidth: 0, // Crucial for flexbox to allow children to scroll horizontally
          minHeight: 'calc(100vh - 64px)',
          background: '#f9fafb',
          position: 'relative',
          overflowX: 'hidden', // Prevent the main container from scrolling horizontally
          overflowY: 'auto'
        }}
      >
        {children}
      </Box>
      {(userRole === 'Company Admin' || userRole === 'Super Admin') && (
        <>
          <div style={{ position: 'fixed', bottom: '75px', [isRTL ? 'left' : 'right']: '16px', zIndex: 1000 }}>
            <Zoom in={true} unmountOnExit>
              <Fab
                size="small" // Decrease the size of the FAB
                color="primary"
                aria-label="chat"
                onClick={toggleChatbot}
                sx={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                  boxShadow: '0 4px 14px rgba(14, 165, 233, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0c85b9 0%, #c53ac0 100%)',
                    transform: 'scale(1.1)' // Enlarge on hover
                  }
                }}
              >
                <ChatIcon fontSize="small" /> 
              </Fab>
            </Zoom>
          </div>

          <div
            style={isChatbotEnlarged ? { ...baseChatbotStyle, ...enlargedChatbotStyle } : baseChatbotStyle}
          >
            <ChatbotContainer 
              isEnlarged={isChatbotEnlarged}
              onToggleEnlarge={handleToggleChatbotEnlarge}
            />
          </div>
        </>
      )}
    </Box>
  );
}
